import { useGetV1TenantsIdEntitlements } from "@solarkey/api-client";
import { EmptyState, ErrorState, Skeleton } from "@solarkey/ui";

import { PageHeader } from "../shell/PageHeader";
import { useCapabilities } from "../session/capabilities";

/* ════════════════════════════════════════════════════════════════════════════
   Admin — Entitlements (UX-005 §4.10).

   Everything on this screen now comes from GET /v1/tenants/{id}/entitlements:
   the tier and the `flags` map. The capability table used to be a hardcoded
   12 × 6 grid of invented cells while the real `flags` map was fetched and
   thrown away; the tier fell back to the HIGHEST tier ("Easy U") when the
   platform did not state one, which is a security-relevant default, not a
   cosmetic one. Both are gone.

   What the endpoint does NOT serve is withheld with a reason, never filled in:
     * the tier BUNDLE matrix (which tier includes which capability) lives in the
       backend's `_TIER_FLAGS` and is not in the response;
     * the date a flag was set — `feature_flag.created_at` exists in the catalog
       but is not served;
     * the rail preview per role, and the "if this tenant were tier X" simulation,
       have no resolver behind them.
   ════════════════════════════════════════════════════════════════════════════ */

/** Plain words for the flag keys the platform serves. Unknown keys humanise. */
const FLAG_LABEL: Record<string, string> = {
  lens_energy: "Energy lens",
  lens_equipment: "Equipment lens",
  lens_value: "Value lens",
  lens_contracts: "Contracts lens",
  lens_work: "Work lens",
  lens_control: "Control lens",
};

function flagLabel(key: string): string {
  const known = FLAG_LABEL[key];
  if (known) return known;
  const words = key.replace(/_/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** "easy_m" → "Easy M". Never invents a tier when the platform states none. */
function tierLabel(tier: string | undefined): string | null {
  if (!tier) return null;
  const m = /^easy[_ -]?([mtu])$/i.exec(tier.trim());
  if (m) return `Easy ${m[1]!.toUpperCase()}`;
  return tier;
}

export function Admin({ tenant }: { tenant: string }): React.ReactNode {
  const caps = useCapabilities();
  const query = useGetV1TenantsIdEntitlements(tenant);

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-density-pad" aria-busy="true">
        <Skeleton variant="text" width="12rem" />
        <Skeleton variant="row" lines={8} />
      </div>
    );
  }

  if (query.isError || !query.data || query.data.status !== 200) {
    return (
      <ErrorState
        title="Admin unavailable"
        message="Entitlements could not be loaded."
        onRetry={() => void query.refetch()}
      />
    );
  }

  const d = query.data.data.data;
  const tier = tierLabel(d.tier);
  const flags = (d.flags ?? {}) as Record<string, unknown>;
  const flagRows = Object.entries(flags)
    .map(([key, value]) => ({ key, on: value === true }))
    .sort((a, b) => flagLabel(a.key).localeCompare(flagLabel(b.key)));

  // Tenant name comes from the session (never written into the app) — AMD-002 §8 item 9.
  const tenantName =
    caps.accessible_tenants.find((t) => t.slug === tenant || t.id === tenant)?.name ?? tenant;

  return (
    <div className="flex flex-col gap-density-pad">
      <PageHeader
        title="Entitlements"
        family="admin"
        scope={
          tier
            ? `${tenantName} · ${tier}`
            : `${tenantName} · tier not stated by the platform`
        }
        action={
          <span className="sk-managed-badge">Managed by SolarKey operations</span>
        }
      />

      <div className="sk-entitlements-cols">
        {/* Left: the real flags for this tenant */}
        <section aria-labelledby="tier-flags" className="sk-section">
          <div className="sk-section__head">
            <div>
              <h2 id="tier-flags" className="sk-section__title">Tier and flags</h2>
              <p className="sk-section__sub">
                as served for this tenant · the tier is the ceiling over the flags
              </p>
            </div>
          </div>

          {flagRows.length === 0 ? (
            <EmptyState
              variant="no_data"
              title="No entitlement flags served for this tenant"
              message="The endpoint returned no flags, so none are shown."
            />
          ) : (
            <table className="sk-ent-table">
              <thead>
                <tr>
                  <th className="sk-ent-table__cap">CAPABILITY</th>
                  <th className="sk-ent-table__status">THIS TENANT</th>
                </tr>
              </thead>
              <tbody>
                {flagRows.map((row) => (
                  <tr key={row.key}>
                    <td className="sk-ent-table__name">{flagLabel(row.key)}</td>
                    <td className="sk-ent-table__dot">
                      {row.on ? (
                        <span className="sk-ent-status sk-ent-status--on">
                          <span className="sk-ent-status__dot" />
                          on
                        </span>
                      ) : (
                        <span className="sk-ent-status sk-ent-status--off">
                          <span className="sk-ent-dot sk-ent-dot--off" />
                          off
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <p className="sk-section__sub" style={{ padding: "8px var(--sk-card-pad)" }}>
            The per-tier bundle columns (which tier includes which capability) and the
            date each flag was set are not withheld by choice — the entitlements
            response carries neither, so this table states only what it was told.
          </p>
        </section>

        {/* Right: what this screen cannot state yet */}
        <div className="sk-entitlements-right">
          <section aria-labelledby="not-served" className="sk-section">
            <div className="sk-section__head">
              <h2 id="not-served" className="sk-section__title">Not stated here</h2>
              <span className="sk-section__sub">no resolver behind it yet</span>
            </div>

            <div className="sk-rail-preview-grid">
              <div className="sk-rail-preview-item">
                <span className="sk-rail-preview-role">TIER BUNDLES</span>
                <span className="sk-rail-preview-families">
                  Which capability each tier includes is decided in the platform and
                  not returned by the entitlements endpoint.
                </span>
              </div>
              <div className="sk-rail-preview-item">
                <span className="sk-rail-preview-role">FLAG HISTORY</span>
                <span className="sk-rail-preview-families">
                  When a flag was set is recorded against the flag in the catalog but
                  is not served.
                </span>
              </div>
              <div className="sk-rail-preview-item">
                <span className="sk-rail-preview-role">RAIL PREVIEW</span>
                <span className="sk-rail-preview-families">
                  What the navigation rail shows per role, and which families are
                  locked, is resolved per session — this screen cannot preview another
                  role&apos;s rail.
                </span>
              </div>
              <div className="sk-rail-preview-item">
                <span className="sk-rail-preview-role">TIER SIMULATION</span>
                <span className="sk-rail-preview-families">
                  &quot;If this tenant were on another tier&quot; needs the bundle
                  matrix and the grant resolver; neither is served.
                </span>
              </div>
              <div className="sk-rail-preview-item">
                <span className="sk-rail-preview-role">ADD-ON REQUESTS</span>
                <span className="sk-rail-preview-families">
                  There is no request action on this screen yet. Entitlements are
                  changed by SolarKey operations.
                </span>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
